import { createPortal } from 'react-dom';

import '../../../JxControls/JxTheme.css';

import { JxButton, JxFilterEdit, JxFormResizableBase, JxTitleBar, OpenToast } from '@jx';

// controls
import { jxScrollbarThin } from '../../../JxControls/jxScrollbarStyle';
import componentNotifier from '../../../services/wss/ComponentNotifier';
import { EngineNames } from '../../../services/wss/events/EngineNames';

// core
import umlPkgLibEngine from '../Core/UmlPkgLibEngine';
import UmlPkgLibInserter from '../Core/UmlPkgLibInserter';
import umlCanvasEngine from '../../Core/UmlCanvasEngine.js';
import umlConnectorEngine from '../../Core/UmlConnectorEngine.js';
import umlShapeSerializer from '../../Core/UmlShapeSerializer.js';

// locals
import { TITLE_BAR_ACTIONS } from './UmlPkgLibViewerDlgTitleBarActions';
import { createSchema, createEmpty } from './UmlPkgLibViewerDlgSchema';
import UmlPkgLibViewerRow from './UmlPkgLibViewerRow.jsx';
import './UmlPkgLibViewerDlg.css';

/**
 * UmlPkgLibViewerDlg — lists saved UmlPkgLib notes (package templates). Clicking
 * an item inserts it into the current canvas via UmlPkgLibInserter — does NOT
 * close the dialog, so several templates can be inserted in a row.
 * @param {{ open: boolean, onClose: Function }} props
 */
export default class UmlPkgLibViewerDlg extends JxFormResizableBase {
    constructor(props) {
        super(props);
        this.name = 'UmlPkgLibViewerDlg';
        this.schema = createSchema();
        this.state = { ...this.state, inputs: createEmpty() };
    }

    componentDidMount() {
        super.componentDidMount();
        this.bringToTop();
    }

    componentWillUnmount() {
        super.componentWillUnmount();
    }

    componentDidUpdate(prevProps) {
        super.componentDidUpdate(prevProps);
        if (this.props.open && !prevProps.open) {
            this.setState({ inputs: createEmpty() });
        }
    }

    getDefaultGeo() {
        return { top: 180, left: 138, width: 389, height: 291 };
    }

    handleClose = (action) => {
        try {
            this.props.onClose?.();
        } catch (error) {
            this.logError('handleClose', error.message);
        }
    };

    /**
     * Inserts the selected template into the current canvas (UmlPkgLibInserter),
     * reconstructs plain shapes/connectors into real class instances
     * (UmlShapeSerializer), and adds them to the engines. Stays open on
     * success — a "not inserted, name already exists" toast if the target
     * schema already has a top-level package with this name.
     * @param {object} note
     */
    handleSelect = (note) => {
        try {
            const template = JSON.parse(note.body);
            const result = UmlPkgLibInserter.insert(template, umlCanvasEngine.shapes);
            if (!result) {
                OpenToast(`A package named "${template.package.name}" already exists \u2014 not inserted`, undefined, {
                    className: 'jx-toast-error',
                });
                return;
            }
            result.shapes.forEach((plainShape) => {
                const shape = umlShapeSerializer.fromPlainShape(plainShape);
                if (shape) umlCanvasEngine.addShape(shape);
            });
            result.connectors.forEach((plainConnector) => {
                const connector = umlShapeSerializer.fromPlainConnector(plainConnector);
                if (connector) umlConnectorEngine.addConnector(connector);
            });
            OpenToast(`Package "${template.package.name}" inserted`, undefined, { className: 'jx-toast-accent' });
        } catch (error) {
            this.logError('handleSelect', error.message);
        }
    };

    getFilteredItems() {
        const items = umlPkgLibEngine.getAll();
        const filter = this.state.inputs.name;
        if (!filter) return items;
        const needle = filter.toLowerCase();
        return items.filter(function matchFilter(item) {
            return (item.title || '').toLowerCase().includes(needle);
        });
    }

    render() {
        if (!this.props.open) return null;

        const items = this.getFilteredItems();

        const panel = (
            <div {...this.panelProps} style={this.panelStyle} className={`jx-panel upld-panel ${this.panelActiveClass}`}>
                <JxTitleBar
                    id={`${this.name}_titlebar`}
                    title="Package Library"
                    variant="toolwindow"
                    isDraggable={true}
                    dragHandleProps={this.dragHandleProps}
                    actions={TITLE_BAR_ACTIONS(() => this.handleClose(-1))}
                />

                <div className="upld-filter">
                    <JxFilterEdit
                        id="upld_filter"
                        value={this.state.inputs.name}
                        onChange={(v) => this.handleChange('name', v)}
                        placeholder="Filter packages..."
                    />
                </div>

                <div className={`upld-body ${jxScrollbarThin}`}>
                    {items.length === 0 && <div className="upld-empty">No packages saved to the library yet.</div>}
                    {items.map((item) => (
                        <UmlPkgLibViewerRow key={item.rk} item={item} isSelected={false} onSelect={this.handleSelect} />
                    ))}
                </div>

                <div className="upld-footer">
                    <JxButton label="Close" onClick={() => this.handleClose(0)} />
                </div>

                {this.renderResizeHandles()}
            </div>
        );

        return createPortal(panel, document.body);
    }
}
